'use client';

import { useState } from 'react';
import { Button, TextField, Typography, Snackbar, IconButton, InputAdornment 
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginLayout from './LoginLayout';
import { useTranslation } from '../common/components/LocalizationProvider';
import { snackBarDurationShortMs } from '../common/util/duration';
import { useCatch } from '../reactHelper';
import BackIcon from '../common/components/BackIcon';

import LockIcon from "@mui/icons-material/Lock"
import EmailIcon from "@mui/icons-material/Email"
import { useTheme } from "@mui/material/styles"
import fetchOrThrow from '../common/util/fetchOrThrow';

const useStyles = makeStyles()((theme) => ({
  options: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    zIndex: 10,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    padding: theme.spacing(4),
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    maxWidth: "400px",
    width: "100%",
    position: "relative",
    zIndex: 1,
  },
  background: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: theme.palette.mode === "dark" ? "url(/dark-background.jpg)" : "url(/light-background.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    zIndex: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: theme.spacing(3),
    fontWeight: 500,
    marginLeft: theme.spacing(1),
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  input: {
    width: "100%",
    padding: "12px 20px",
    margin: "8px 0",
    boxSizing: "border-box",
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
}));

const ResetPasswordPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme()

  const [email, setEmail] = useState('');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('passwordReset');

  const handleSubmit = useCatch(async (event) => {
    event.preventDefault();
    if (!token) {
      await fetchOrThrow('/api/password/reset', {
        method: 'POST',
        body: new URLSearchParams(`email=${encodeURIComponent(email)}`),
      });
    } else {
      await fetchOrThrow('/api/password/update', {
        method: 'POST',
        body: new URLSearchParams(`token=${encodeURIComponent(token)}&password=${encodeURIComponent(password)}`),
      });
    }
    setSnackbarOpen(true);
  });

  return (
    <LoginLayout>
      <div className={classes.background} />
      <div className={classes.container}>
        <div className={classes.header}>
          <IconButton color="primary" onClick={() => navigate('/login')}>
            <BackIcon />
          </IconButton>
          <Typography className={classes.title} color="primary">
            {t('loginReset')}
          </Typography>
        </div>

        {!token ? (
          <TextField
            required
            placeholder={t("userEmail")}
            name="email"
            value={email}
            autoComplete="email"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
              className: classes.input,
            }}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiInputAdornment-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
            }}
          />
        ) : (
          <TextField
            required
            placeholder={t("userPassword")}
            name="password"
            value={password}
            type="password"
            autoComplete="current-password"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              className: classes.input,
            }}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiInputAdornment-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
            }}
          />
        )}

        <Button
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSubmit}
          disabled={(!token && !/(.+)@(.+)\.(.{2,})/.test(email)) || (token && !password)}
          fullWidth
          className={classes.submitButton}
        >
          {t('loginReset')}
        </Button>
      </div>
      <Snackbar
        open={snackbarOpen}
        onClose={() => navigate('/login')}
        autoHideDuration={snackBarDurationShortMs}
        message={!token ? t('loginResetSuccess') : t('loginUpdateSuccess')}
      />
    </LoginLayout>
  );
};

export default ResetPasswordPage;
